import { useCallback, useEffect, useState } from "react";

import { isSupabaseConfigured, supabase } from "../supabaseClient";

import type { Session, User } from "@supabase/supabase-js";

export interface UserProfile {
  id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  updated_at: string | null;
}

type ProfileUpdates = Partial<Pick<UserProfile, "full_name" | "avatar_url">>;

const PROFILE_FIELDS = "id,email,full_name,avatar_url,updated_at";

const getProfile = async (client: NonNullable<typeof supabase>, user: User) => {
  return client
    .from("profiles")
    .select(PROFILE_FIELDS)
    .eq("id", user.id)
    .maybeSingle();
};

export const useSupabaseAuth = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async (user: User | null) => {
    if (!user) {
      setProfile(null);
      return;
    }

    if (!isSupabaseConfigured || !supabase) {
      setError("Supabase 未配置，请检查环境变量。");
      return;
    }

    setProfileLoading(true);
    const { data, error: profileError } = await getProfile(supabase, user);
    if (profileError) {
      setError(profileError.message);
    } else {
      setProfile(data);
    }
    setProfileLoading(false);
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false);
      setError("Supabase 未配置，请检查环境变量。");
      return;
    }

    let isActive = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!isActive) {
          return;
        }
        setSession(data.session);
        setLoading(false);
        loadProfile(data.session?.user ?? null);
      })
      .catch((sessionError) => {
        if (!isActive) {
          return;
        }
        setError(sessionError.message);
        setLoading(false);
      });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (import.meta.env.DEV) {
          console.log("[useSupabaseAuth] Auth state changed:", event);
          console.log(
            "[useSupabaseAuth] User ID:",
            nextSession?.user?.id || "N/A",
          );
          console.log(
            "[useSupabaseAuth] User email:",
            nextSession?.user?.email || "N/A",
          );
        }
        setSession(nextSession);
        loadProfile(nextSession?.user ?? null);
      },
    );

    return () => {
      isActive = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    if (!isSupabaseConfigured || !supabase) {
      setError(
        "Supabase is not configured. Please check environment variables.",
      );
      return;
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/app`,
        skipBrowserRedirect: true,
      },
    });

    if (error) {
      setError(error.message);
      return;
    }

    if (data?.url) {
      window.location.assign(data.url);
    }
  }, []);

  /**
   * Send a 6-digit OTP code via email. Returns `true` when the email
   * was sent successfully, `false` on any error.
   */
  const sendOtp = useCallback(async (email: string): Promise<boolean> => {
    setError(null);
    if (!isSupabaseConfigured || !supabase) {
      setError(
        "Supabase is not configured. Please check environment variables.",
      );
      return false;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // We still set this so Magic Link in the email also works as fallback
        emailRedirectTo: `${window.location.origin}/app`,
        shouldCreateUser: true,
      },
    });

    if (error) {
      setError(error.message);
      return false;
    }
    return true;
  }, []);

  /**
   * Verify the 6-digit OTP code. Returns `true` on success (session
   * is automatically set via onAuthStateChange), `false` on error.
   */
  const verifyOtp = useCallback(
    async (email: string, token: string): Promise<boolean> => {
      setError(null);
      if (!isSupabaseConfigured || !supabase) {
        setError(
          "Supabase is not configured. Please check environment variables.",
        );
        return false;
      }

      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "email",
      });

      if (error) {
        setError(error.message);
        return false;
      }
      return true;
    },
    [],
  );

  const updateProfile = useCallback(
    async (updates: ProfileUpdates) => {
      setError(null);
      if (!supabase) {
        setError(
          "Supabase is not configured. Please check environment variables.",
        );
        return;
      }
      if (!session?.user) {
        setError("Please sign in first.");
        return;
      }

      const payload = {
        id: session.user.id,
        email: session.user.email ?? null,
        ...updates,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("profiles")
        .upsert(payload)
        .select(PROFILE_FIELDS)
        .single();

      if (error) {
        setError(error.message);
      } else {
        setProfile(data);
      }
    },
    [session?.user],
  );

  const signOut = useCallback(async () => {
    setError(null);
    if (!isSupabaseConfigured || !supabase) {
      setError(
        "Supabase is not configured. Please check environment variables.",
      );
      return;
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      setError(error.message);
    }
  }, []);

  return {
    session,
    profile,
    loading,
    profileLoading,
    error,
    setError,
    signInWithGoogle,
    sendOtp,
    verifyOtp,
    updateProfile,
    signOut,
  };
};
